/*
 * Back in stock notification for the product page.
 *
 * Swaps the disabled "Sold out" button for a "Notify me" button whenever the
 * selected variant is unavailable, and collects the shopper's email in a popup.
 *
 * The popup is reparented to <body> (see moveModalToBody), so every listener is
 * delegated from the document instead of bound to a captured child node: that
 * keeps the popup working after the move and after product-info re-renders the
 * section.
 *
 * Submission targets, in order of preference:
 *   1. data-form-endpoint      - any endpoint that accepts { email, variant_id, product_id }
 *   2. Klaviyo                 - company id from data-klaviyo-company-id, or from the
 *                                onsite klaviyo.account() call when that is blank
 *   3. data-contact-url        - the native Shopify contact form (no app required)
 */
if (!customElements.get('back-in-stock')) {
  customElements.define(
    'back-in-stock',
    class BackInStock extends HTMLElement {
      connectedCallback() {
        this.modalId = `ProductEmailBackInStock-${this.dataset.sectionId}`;
        this.moveModalToBody();

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        document.addEventListener('click', this.onDocumentClick.bind(this), { signal });
        document.addEventListener('submit', this.onDocumentSubmit.bind(this), { signal });
        document.addEventListener('keyup', this.onKeyUp.bind(this), { signal });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }

        // product-info publishes variantChange only when a variant was resolved. When
        // the shopper picks a combination that does not exist it calls setUnavailable()
        // and returns, so without this the element would keep whatever state the
        // previous variant left behind — hiding add to cart with no way back.
        this.variantIdInput?.addEventListener('change', this.onVariantInputChange.bind(this), { signal });
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.variantChangeUnsubscriber?.();
        this.modal?.remove();
        document.body.classList.remove('overflow-hidden');
      }

      /*
       * The product info column is animated with a transform, which turns it into the
       * containing block for position: fixed. Reparent the dialog to the body so the
       * overlay covers the viewport and the popup centres against it.
       */
      moveModalToBody() {
        const modal = this.querySelector('.productEmailBackInStock');
        if (modal) document.body.appendChild(modal);
      }

      get modal() {
        return document.getElementById(this.modalId);
      }

      get trigger() {
        return this.querySelector('.productEmailBackInStockTrigger');
      }

      get form() {
        return this.modal?.querySelector('.productEmailBackInStockForm');
      }

      get input() {
        return this.modal?.querySelector('.productEmailBackInStockFormInput');
      }

      get errorMessage() {
        return this.modal?.querySelector('.productEmailBackInStockFormError');
      }

      get success() {
        return this.modal?.querySelector('.productEmailBackInStockSuccess');
      }

      get productForm() {
        return this.closest('product-form');
      }

      get variantIdInput() {
        return this.productForm?.querySelector('input[name="id"]');
      }

      onVariantInputChange(event) {
        // An empty value means no variant matches the selected options.
        this.guard(() => {
          if (!event.target.value) this.toggle(false);
        });
      }

      /*
       * product-info disables the buy button at the start of every option change and
       * only re-enables it at the end of an unguarded chain. This element is called
       * from inside that chain twice — from the id input's change event, and from the
       * variantChange subscriber, which pubsub invokes with a bare .map() so one throw
       * stops every later subscriber too. Nothing this element does is important
       * enough to leave the buy button stuck disabled, so it never throws outward.
       */
      guard(fn) {
        try {
          fn();
        } catch (error) {
          console.error('[back-in-stock]', error);
        }
      }

      onDocumentClick(event) {
        const target = event.target;

        if (this.contains(target) && target.closest('.productEmailBackInStockTrigger')) {
          this.open();
          return;
        }

        const closer = target.closest('[data-back-in-stock-close]');
        if (closer && this.modal?.contains(closer)) this.close();
      }

      onDocumentSubmit(event) {
        const form = event.target.closest?.('.productEmailBackInStockForm');
        if (!form || !this.modal?.contains(form)) return;

        event.preventDefault();
        this.onSubmit(event.submitter);
      }

      onKeyUp(event) {
        if (event.code !== 'Escape') return;
        this.close();
      }

      onVariantChange(payload) {
        this.guard(() => {
          const data = payload?.data;
          if (!data || data.sectionId !== this.dataset.sectionId) return;

          const variant = data.variant;
          this.dataset.variantId = variant?.id || '';
          this.toggle(Boolean(variant) && variant.available === false);
          this.reset();
        });
      }

      /* Shows the notify button and hides the add to cart / payment buttons, or the reverse. */
      toggle(isUnavailable) {
        this.classList.toggle('hidden', !isUnavailable);
        this.productForm?.toggleAttribute('data-back-in-stock-active', isUnavailable);

        if (!isUnavailable) this.close();
      }

      reset() {
        this.form?.removeAttribute('hidden');
        this.form?.classList.add('active');
        this.success?.setAttribute('hidden', '');
        this.handleErrorMessage();
        if (this.input) this.input.value = '';
      }

      open() {
        this.modal?.removeAttribute('hidden');
        this.trigger?.setAttribute('aria-expanded', 'true');
        document.body.classList.add('overflow-hidden');
        this.input?.focus();
      }

      close() {
        if (!this.modal || this.modal.hasAttribute('hidden')) return;

        this.modal.setAttribute('hidden', '');
        this.trigger?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('overflow-hidden');
        this.trigger?.focus();
      }

      handleErrorMessage(message) {
        const errorMessage = this.errorMessage;
        if (!errorMessage) return;

        errorMessage.toggleAttribute('hidden', !message);
        errorMessage.textContent = message || '';
      }

      async onSubmit(submitter) {
        const input = this.input;
        if (!input) return;

        const email = input.value.trim();
        if (!email || !input.checkValidity()) {
          input.reportValidity();
          return;
        }

        this.handleErrorMessage();
        submitter?.setAttribute('disabled', 'disabled');
        submitter?.classList.add('loading');

        try {
          await this.subscribe(email);
          this.showSuccess();
        } catch (error) {
          console.error('[back-in-stock]', error);
          this.handleErrorMessage(window.backInStockStrings?.error || 'Something went wrong. Please try again.');
        } finally {
          submitter?.removeAttribute('disabled');
          submitter?.classList.remove('loading');
        }
      }

      async subscribe(email) {
        const { formEndpoint } = this.dataset;
        if (formEndpoint) return this.subscribeViaEndpoint(formEndpoint, email);

        const companyId = await this.klaviyoCompanyId();
        if (companyId) return this.subscribeViaKlaviyo(companyId, email);

        return this.subscribeViaContactForm(email);
      }

      /* The onsite Klaviyo script knows the company id, so no key needs configuring. */
      async klaviyoCompanyId() {
        if (this.dataset.klaviyoCompanyId) return this.dataset.klaviyoCompanyId;

        try {
          return (await window.klaviyo?.account()) || '';
        } catch (error) {
          console.error('[back-in-stock] klaviyo.account() failed', error);
          return '';
        }
      }

      async subscribeViaEndpoint(endpoint, email) {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            email,
            product_id: this.dataset.productId,
            variant_id: this.dataset.variantId,
          }),
        });

        return this.constructor.ensureOk(response);
      }

      async subscribeViaKlaviyo(companyId, email) {
        const response = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(companyId)}`,
          {
            method: 'POST',
            headers: {
              accept: 'application/vnd.api+json',
              'content-type': 'application/vnd.api+json',
              revision: '2024-10-15',
            },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  channels: ['EMAIL'],
                  profile: { data: { type: 'profile', attributes: { email } } },
                },
                relationships: {
                  variant: {
                    data: {
                      type: 'catalog-variant',
                      id: `$shopify:::$default:::${this.dataset.variantId}`,
                    },
                  },
                },
              },
            }),
          }
        );

        return this.constructor.ensureOk(response);
      }

      /* Fallback that works without any app: posts the request through Shopify's contact form. */
      async subscribeViaContactForm(email) {
        const body = new FormData();
        body.append('form_type', 'contact');
        body.append('utf8', '✓');
        body.append('contact[email]', email);
        body.append('contact[body]', this.contactBody());

        const response = await fetch(this.dataset.contactUrl, { method: 'POST', body });
        return this.constructor.ensureOk(response);
      }

      contactBody() {
        const template = window.backInStockStrings?.body || '';
        return `${template.replace('[title]', this.dataset.productTitle)} (variant ${this.dataset.variantId})`;
      }

      showSuccess() {
        this.form?.classList.remove('active');
        this.form?.setAttribute('hidden', '');
        this.success?.removeAttribute('hidden');
        this.success?.classList.add('active');
      }

      static async ensureOk(response) {
        if (response.ok) return response;

        const body = await response.text().catch(() => '');
        throw new Error(`Back in stock subscription failed (${response.status} ${response.url}): ${body.slice(0, 300)}`);
      }
    }
  );
}
